import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './S_InviteCoauthorsPopup.module.css';
import A_SearchInput from '@/components/A_Atoms/A_SearchInput/A_SearchInput';
import A_Button from '@/components/A_Atoms/A_Button/A_Button';
import A_CopyLinkButton from '@/components/A_Atoms/A_CopyLinkButton/A_CopyLinkButton';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';
import A_SmallInput from '@/components/A_Atoms/A_SmallInput/A_SmallInput';
import A_Notification from '@/components/A_Atoms/A_Notification/A_Notification';
import A_GlassTooltip from '@/components/A_Atoms/A_GlassTooltip/A_GlassTooltip';
import { User } from '@/types/user';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslation } from '@/constants/translations';
import O_SearchResults from '@/components/O_Organisms/O_SearchResults/O_SearchResults';
import { useIsMobile } from '@/hooks/useIsMobile';
import cn from 'classnames';
import { usersApi, mapSearchResultToUser } from '@/services/usersApi';
import { capsulesApi } from '@/services/capsulesApi';
import { useUpdateCoauthors, useUpdateAllowedUsers } from '@/hooks/useCapsules';

interface S_InviteCoauthorsPopupProps {
  setIsInviteCoauthorsPopupOpen: (isOpen: boolean) => void;
  onUserSelect?: (user: User) => void;
  onEmailInvite?: (email: string) => void | Promise<void>;
  existingAllowedUserIds?: number[];
  onAllowedUsersSynced?: (userIds: string[]) => void;
  capsuleId?: string | number;
  mode?: 'coauthor' | 'viewer';
}

export default function S_InviteCoauthorsPopup({
  setIsInviteCoauthorsPopupOpen,
  onUserSelect,
  onEmailInvite,
  existingAllowedUserIds = [],
  onAllowedUsersSynced,
  capsuleId,
  mode = 'coauthor',
}: S_InviteCoauthorsPopupProps) {
  const { language } = useLanguage();
  const isMobile = useIsMobile(768);
  const t = useCallback(
    (key: string) => getTranslation(language, key),
    [language]
  );
  const [email, setEmail] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const updateCoauthorsMutation = useUpdateCoauthors();
  const updateAllowedUsersMutation = useUpdateAllowedUsers();
  const formWrapperRef = useRef<HTMLDivElement>(null);

  const dismissSearchResults = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
  }, []);

  useEffect(() => {
    const hasPanel = searchResults.length > 0 && searchQuery.trim().length > 0;
    if (!hasPanel) return;

    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const el = formWrapperRef.current;
      const target = e.target as Node | null;
      if (el && target && !el.contains(target)) {
        dismissSearchResults();
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [dismissSearchResults, searchQuery, searchResults.length]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (searchResults.length === 0 && !searchQuery.trim()) return;
      dismissSearchResults();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dismissSearchResults, searchQuery, searchResults.length]);

  const handleClosePopup = useCallback(() => {
    setIsInviteCoauthorsPopupOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  }, [setIsInviteCoauthorsPopupOpen]);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleUserSelect = useCallback(
    async (user: User) => {
      if (capsuleId) {
        try {
          const userId = parseInt(String(user.id), 10);
          if (isNaN(userId)) {
            return;
          }

          if (mode === 'coauthor') {
            await updateCoauthorsMutation.mutate({
              id: capsuleId,
              coauthorsIds: [userId],
            });
          } else {
            const mergedIds = Array.from(
              new Set([...existingAllowedUserIds, userId])
            );
            const updated = await updateAllowedUsersMutation.mutate({
              id: capsuleId,
              allowedUserIds: mergedIds,
            });
            onAllowedUsersSynced?.(
              updated.privacy.allowedUserIds?.map(String) ??
                mergedIds.map(String)
            );
          }
          handleClosePopup();
        } catch {
          setNotification({
            type: 'error',
            message: t('inviteCoauthors.error'),
          });
        }
      } else {
        onUserSelect?.(user);
        handleClosePopup();
      }
    },
    [
      capsuleId,
      mode,
      updateCoauthorsMutation,
      updateAllowedUsersMutation,
      existingAllowedUserIds,
      onAllowedUsersSynced,
      onUserSelect,
      handleClosePopup,
      t,
    ]
  );

  const handleSendEmailInvite = useCallback(async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    try {
      setIsSendingEmail(true);
      if (!capsuleId) {
        if (!onEmailInvite) {
          setNotification({
            type: 'error',
            message: t('inviteCoauthors.error'),
          });
          return;
        }
        await onEmailInvite(trimmed);
      } else if (mode === 'coauthor') {
        await updateCoauthorsMutation.mutate({
          id: capsuleId,
          coauthorsEmails: [trimmed],
        });
      } else {
        const updated = await updateAllowedUsersMutation.mutate({
          id: capsuleId,
          allowedUserIds: [...existingAllowedUserIds],
          allowedUserEmails: [trimmed],
        });
        onAllowedUsersSynced?.(
          updated.privacy.allowedUserIds?.map(String) ??
            existingAllowedUserIds.map(String)
        );
      }
      setEmail('');
      handleClosePopup();
    } catch {
      setNotification({
        type: 'error',
        message: t('inviteCoauthors.error'),
      });
    } finally {
      setIsSendingEmail(false);
    }
  }, [
    capsuleId,
    email,
    mode,
    onEmailInvite,
    t,
    updateCoauthorsMutation,
    updateAllowedUsersMutation,
    existingAllowedUserIds,
    onAllowedUsersSynced,
    handleClosePopup,
  ]);

  useEffect(() => {
    const normalized = searchQuery.trim();

    if (!normalized || normalized.length < 2) {
      setSearchResults([]);
      return;
    }

    const searchUsers = async () => {
      try {
        const response = await usersApi.searchUsers({
          q: normalized,
          limit: 20,
          mutual_only: mode === 'coauthor',
        });

        if (response.success && response.data) {
          setSearchResults(response.data.users.map(mapSearchResultToUser));
        } else {
          setSearchResults([]);
        }
      } catch {
        setSearchResults([]);
      }
    };

    const timeoutId = setTimeout(searchUsers, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, mode]);

  return (
    <div className={styles.overlay}>
      <div className={cn(styles.wrapper, { [styles.mobile]: isMobile })}>
        {isMobile && <div className={styles.handle}></div>}
        <div className={styles.content}>
          <div className={styles.title_row}>
            <h4 className={styles.title}>{t('inviteCoauthors.title')}</h4>
            {mode === 'coauthor' && (
              <A_GlassTooltip content={t('inviteCoauthors.custodianHint')}>
                <button type='button' className={styles.help_btn}>
                  <span className={styles.help_mark} aria-hidden>
                    ?
                  </span>
                </button>
              </A_GlassTooltip>
            )}
          </div>
          <div ref={formWrapperRef} className={styles.form_wrapper}>
            <div className={styles.search_block}>
              <A_SearchInput
                placeholder={t('inviteCoauthors.searchPlaceholder')}
                value={searchQuery}
                onChange={handleSearchChange}
                onSearch={handleSearchChange}
                autoFocus
                alwaysExpanded
                maxWidth='100%'
              />
              {searchResults.length > 0 && searchQuery.trim().length > 0 && (
                <O_SearchResults
                  results={searchResults}
                  query={searchQuery}
                  onUserSelect={handleUserSelect}
                />
              )}
            </div>
            {(!isMobile || !searchQuery.trim()) && (
              <>
                <div className={styles.input_wrapper}>
                  <A_SmallInput
                    placeholder={t('inviteCoauthors.emailPlaceholder')}
                    background='white'
                    label=''
                    value={email}
                    onChange={setEmail}
                  />
                  <A_Button
                    variant='tertiary'
                    size='small'
                    width={isMobile ? '100%' : undefined}
                    fontSize='text-p-4'
                    onClick={handleSendEmailInvite}
                    disabled={!email.trim() || isSendingEmail}
                  >
                    {t('inviteCoauthors.sendInvite')}
                  </A_Button>
                </div>
              </>
            )}
          </div>
          {(!isMobile || !searchQuery.trim()) && (
            <div className={styles.copy_link_button_wrapper}>
              <A_CopyLinkButton
                getLinkToCopy={
                  capsuleId && mode === 'coauthor'
                    ? async () => {
                        const res =
                          await capsulesApi.createCoauthorReferralLink(
                            capsuleId
                          );
                        if (res.success && res.data?.referralLink) {
                          return res.data.referralLink;
                        }
                        throw new Error(
                          'Не удалось получить реферальную ссылку'
                        );
                      }
                    : undefined
                }
              />
            </div>
          )}
          <div className={styles.actions_row}>
            <A_Button
              variant='tertiary'
              size='small'
              width={isMobile ? '100%' : undefined}
              fontSize='text-p-4'
              onClick={handleClosePopup}
              icon={
                !isMobile ? (
                  <Q_Icon.CrossIcon width={24} height={24} fill='none' />
                ) : undefined
              }
              showIcon={!isMobile}
            >
              {t('common.close')}
            </A_Button>
          </div>
        </div>
      </div>
      {notification && (
        <A_Notification
          type={notification.type}
          message={notification.message}
          isVisible={!!notification}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
}
